import { isAxiosError } from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { AUTH_ERROR_CODES, VIEW_API_PERMISSION, isAppView } from '@aegislab/shared';
import { Sentry } from '../lib/sentry';
import { getWindowTag, toEndpoint } from '../lib/sentryTags';
import { isRequestCanceledError } from './requestCancel';
import { RefreshLockTimeoutError } from './refreshLock';

const AUTH_ENDPOINT_PREFIXES = ['/auth/login', '/auth/register', '/auth/logout'];

const EXPECTED_LOGIN_FAILURE_CODES = new Set<string>([
  AUTH_ERROR_CODES.INVALID_CREDENTIALS,
  AUTH_ERROR_CODES.USER_NOT_FOUND,
  AUTH_ERROR_CODES.INVALID_OTP,
]);

// 호출부가 실패를 화면에 표시하지 않는 보조 요청. 탭 숨김 근거까지 있을 때만 낮춘다.
const BACKGROUND_ENDPOINTS = new Set([
  '/presence/heartbeat',
  '/presence/leave',
  '/page-views',
  '/telemetry-events',
]);

const SENSITIVE_KEY_PATTERN = /password|token|secret|otp|refresh/i;

export function isAuthEndpoint(config: InternalAxiosRequestConfig | undefined): boolean {
  const url = config?.url;
  if (!url) return false;
  return AUTH_ENDPOINT_PREFIXES.some((prefix) => url.startsWith(prefix));
}

/** 서버가 실어 보낸 도메인 오류 코드. 응답 본문이 계약 밖일 수 있어 캐스팅을 한 곳에 모은다. */
function readErrorCode(error: AxiosError): string | undefined {
  return (error.response?.data as { errorCode?: string } | undefined)?.errorCode;
}

export function shouldCaptureApiError(error: AxiosError): boolean {
  // 취소는 실패가 아니다. abort 된 요청은 응답이 없어 아래 network error 갈래(!status)에
  // 그대로 걸리므로 그 앞에서 걸러낸다. 이 함수가 인터셉터(client.ts) 캡처의 게이트다.
  if (isRequestCanceledError(error)) return false;

  const status = error.response?.status;

  // 응답 없음·offline 자체는 사용자 실패를 버릴 근거가 아니다.
  if (!status) return true;
  if (status >= 500) return true;
  if (status === 403 || status === 409) return true;

  if (status === 401) {
    if (error.config?._retry) return true;
    if (isAuthEndpoint(error.config)) {
      const errorCode = readErrorCode(error);
      if (errorCode && EXPECTED_LOGIN_FAILURE_CODES.has(errorCode)) return false;
      return true;
    }
    return false;
  }

  return false;
}

/**
 * 세션 갱신(/auth/refresh) 실패 사유를 태그 값 하나로 줄인다.
 *
 * 갱신은 인터셉터가 붙지 않은 axios 로 나가 이 모듈의 캡처 경로를 타지 않고, 갱신 실패
 * 지점이 보고하는 것도 갱신 오류가 아니라 그것을 촉발한 원래 요청의 401 이다. 그래서 서버가
 * 구분해 던지는 REFRESH_TOKEN_INVALID·REVOKED·EXPIRED·MISMATCH 가 어디에도 남지 않는다.
 * 이 값을 원래 401 보고에 태그로 얹는 것이 사유를 남기는 유일한 지점이다 (AXIOM-WEB-2).
 */
export function refreshFailureCode(error: unknown): string {
  if (error instanceof RefreshLockTimeoutError) return 'lock_timeout';
  if (!isAxiosError(error)) return 'unknown';
  if (error.code === 'ETIMEDOUT') return 'timeout';
  if (!error.response) return 'network';
  return readErrorCode(error) ?? `http_${error.response.status}`;
}

export function maskSensitivePayload(value: unknown): unknown {
  if (typeof value === 'string') {
    if (!value.startsWith('{') && !value.startsWith('[')) return value;
    try {
      return JSON.stringify(maskSensitivePayload(JSON.parse(value)));
    } catch {
      return value;
    }
  }
  if (!value || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((item) => maskSensitivePayload(item));

  const result: Record<string, unknown> = {};
  for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
    result[key] = SENSITIVE_KEY_PATTERN.test(key) ? '[Filtered]' : maskSensitivePayload(v);
  }
  return result;
}

// timeout(ECONNABORTED·ETIMEDOUT)과 연결 단절은 원인이 달라 태그를 가른다.
function errorType(error: AxiosError): string {
  const status = error.response?.status;
  if (status) return status >= 500 ? 'server' : 'client';
  return error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' ? 'timeout' : 'network';
}

/**
 * 열려 있는 창의 기본 API 가 그 창의 서버 권한 때문에 403 인가 — 창 게이트와 서버 가드가 엇갈린 건이다.
 * 창이 열려 있다는 것만으론 부족해(다른 창의 요청도 같은 시간에 난다) 403 본문의 `requires R:A` 가
 * 포커스 창의 권한표(VIEW_API_PERMISSION)와 같은 경우만 센다. (LAB-130)
 */
function isOpenWindowApiForbidden(error: AxiosError): boolean {
  if (error.response?.status !== 403) return false;
  const windowId = getWindowTag();
  if (!windowId || !isAppView(windowId)) return false;
  const required = VIEW_API_PERMISSION[windowId];
  const message = (error.response.data as { message?: unknown } | undefined)?.message;
  return (
    !!required && message === `Access denied: requires ${required.resource}:${required.action}`
  );
}

export type ExtraTags = Record<string, string>;

export function captureApiError(error: AxiosError, extraTags: ExtraTags = {}): void {
  const status = error.response?.status;
  const endpoint = toEndpoint(error.config?.url ?? '');
  const visibility = error.config?._sentryVisibility ?? 'unknown';
  const hiddenBackground =
    !status &&
    visibility === 'hidden' &&
    BACKGROUND_ENDPOINTS.has(endpoint) &&
    extraTags.user_visible !== 'true';
  // 서버가 4xx 로 답한 거절은 서버 장애가 아니다. 401·403 증가를 봐야 해서 기록은 남기고 등급만 낮춘다.
  const type = errorType(error);

  Sentry.captureException(error, {
    level: hiddenBackground || type === 'client' ? 'warning' : 'error',
    contexts: {
      api: {
        url: error.config?.url,
        method: error.config?.method,
        status,
        baseURL: error.config?.baseURL,
        request: maskSensitivePayload(error.config?.data),
        response: maskSensitivePayload(error.response?.data),
      },
    },
    tags: {
      api_error: 'true',
      request_url: endpoint || 'unknown',
      request_visibility: visibility,
      network_context: hiddenBackground ? 'hidden_background' : 'unrecovered',
      status_code: status ? String(status) : 'network',
      error_type: type,
      // 열려 있는 창의 API 가 403 — 창 게이트와 서버 가드가 엇갈린 건수를 센다(LAB-129).
      ...(isOpenWindowApiForbidden(error) ? { window_api_forbidden: 'true' } : {}),
      ...extraTags,
    },
  });
}
